// DOM-based typing box wired to an Enter-to-submit callback.

export type TypingBox = {
  el: HTMLElement;
  focus(): void;
  setText(text: string): void;
  getText(): string;
  getCenter(): { x: number; y: number };
};

type Opts = {
  // Return `false` (sync or via promise) to keep the current text in the box —
  // used when the submit is rejected (e.g. validation or server error) so the
  // user can edit and retry without re-typing.
  onSubmit: (text: string) => void | boolean | Promise<void | boolean>;
};

export function initTypingBox(el: HTMLElement, { onSubmit }: Opts): TypingBox {
  el.addEventListener('keydown', async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const text = el.innerText;
      if (text.trim()) {
        const result = await onSubmit(text);
        if (result === false) return;
        // Clear via innerHTML — innerText = '' can leave a stray <br> from
        // contenteditable, which defeats the :empty placeholder and sometimes
        // shows the previous text remnant.
        el.innerHTML = '';
      }
    }
  });

  // Prevent rich paste — keep it text-only.
  el.addEventListener('paste', (e) => {
    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain') ?? '';
    document.execCommand('insertText', false, text);
  });

  return {
    el,
    focus() { el.focus(); },
    setText(text: string) {
      if (text === '') el.innerHTML = '';
      else el.innerText = text;
    },
    getText() { return el.innerText; },
    getCenter() {
      const r = el.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    },
  };
}
